/*
 * Copyright (C) 2018 - 2023 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React from 'react';
import styles from './styles.module.css';
import { withConfigContext } from '../../../../../../../../components/ConfigContext';
import { withTranslation } from 'react-i18next';
import { Button, Col, Form, notification, Result, Row, Steps } from 'antd';
import GroupDetails from './GroupDetails';
import { Link } from 'react-router-dom';
import SelectGroupType from './SelectGroupType';
import AttachingRoles from './AttachingRoles';
import { isAuthorized } from '../../../../../../../../services/utils/authorizationHandler';
import axios from 'axios';
import { handleApiError } from '../../../../../../../../services/utils/errorHandler';

const { Step } = Steps;

class CreateGroup extends React.Component {
  deviceType = null;
  constructor(props) {
    super(props);
    this.config = this.props.context;
    this.state = {
      currentStepIndex: 0,
      hierarchy: false,
      isSuccess: false,
      loading: false,
      groupType: 'topLevel',
      groupId: '',
      groupName: '',
      groupOwner: '',
      groupDescription: '',
      selectedKeyId: '',
      parentGroupId: '',
      selectedKeyName: '',
      onConfirmShareGroups: '',
    };
  }
  handleFormSubmit = formValues => {
    this.setState({
      groupName: formValues.groupName,
      groupOwner: formValues.groupOwner,
      groupDescription: formValues.groupDescription,
    });
  };

  handleGroupTypeChange = value => {
    this.setState({ groupType: value });
  };

  updateSelectedKeyId = selectedKeyId => {
    this.setState({ selectedKeyId: selectedKeyId });
  };

  updateSelectedKeyName = selectedKeyName => {
    this.setState({ selectedKeyName: selectedKeyName });
  };

  setNextStep = () => {
    const currentStepIndex = this.state.currentStepIndex + 1;
    this.setState({ currentStepIndex });
    const {
      groupType,
      groupName,
      groupDescription,
      selectedKeyId,
      onConfirmShareGroups,
    } = this.state;
    if (currentStepIndex === 3) {
      if (groupType === 'topLevel') {
        const values = {
          name: groupName,
          description: groupDescription,
        };
        if (onConfirmShareGroups) {
          values.userRoles = onConfirmShareGroups;
        }
        this.addGroup(values);
      } else {
        const values = {
          name: groupName,
          description: groupDescription,
          parentGroupId: selectedKeyId,
        };
        if (onConfirmShareGroups) {
          values.userRoles = onConfirmShareGroups;
        }
        this.addGroupWithRoles(values);
      }
    }
  };

  setPrevStep = () => {
    const currentStepIndex = this.state.currentStepIndex - 1;
    this.setState({ currentStepIndex });
  };

  onConfirmShareGroup = data => {
    this.setState({ onConfirmShareGroups: data });
  };

  addGroup = values => {
    this.setState({
      loading: true,
    });
    const config = this.props.context;
    const { t } = this.props;
    let apiUrl;
    const groupData = {};
    groupData.name = values.name.trim();
    groupData.description = values.description;
    groupData.owner = this.props.context.user;
    groupData.userRoles = values.userRoles;

    if (isAuthorized(config.scopes, ['perm:admin-groups:add'])) {
      apiUrl =
        window.location.origin +
        config.serverConfig.invoker.uri +
        config.serverConfig.invoker.deviceMgt +
        '/groups/roles/share';
    } else if (isAuthorized(config.scopes, ['perm:groups:add'])) {
      apiUrl =
        window.location.origin +
        config.serverConfig.invoker.uri +
        config.serverConfig.invoker.deviceMgt +
        '/groups/roles/share';
    }

    // send request to the invoker
    axios
      .post(apiUrl, groupData, {
        headers: { 'Content-Type': 'application/json' },
      })
      .then(res => {
        if (res.status === 201) {
          this.setState({
            isSuccess: true,
            loading: false,
          });
          notification.success({
            message: t('api_successMsg'),
            duration: 4,
            description: t('api_addGroupMsg'),
          });
        }
      })
      .catch(error => {
        if (error.response.status === 409) {
          handleApiError(
            error,
            t('duplicate_groupExists', { label: groupData.name }),
            t,
          );
        } else {
          handleApiError(
            error,
            t('api_addError', { label: t('home_menu_group') }),
            t,
          );
        }
        this.setState({
          loading: false,
        });
      });
  };

  addGroupWithRoles = values => {
    this.setState({
      loading: true,
    });
    const config = this.props.context;
    const { t } = this.props;
    let apiUrl;
    const groupData = {};
    groupData.name = values.name.trim();
    groupData.description = values.description;
    groupData.owner = this.props.context.user;
    groupData.parentGroupId = values.parentGroupId;
    groupData.userRoles = values.userRoles;

    if (isAuthorized(config.scopes, ['perm:admin-groups:add'])) {
      apiUrl =
        window.location.origin +
        config.serverConfig.invoker.uri +
        config.serverConfig.invoker.deviceMgt +
        '/groups/roles/share';
    } else if (isAuthorized(config.scopes, ['perm:groups:add'])) {
      apiUrl =
        window.location.origin +
        config.serverConfig.invoker.uri +
        config.serverConfig.invoker.deviceMgt +
        '/groups/roles/share';
    }

    // send request to the invoker
    axios
      .post(apiUrl, groupData, {
        headers: { 'Content-Type': 'application/json' },
      })
      .then(res => {
        if (res.status === 201) {
          this.setState({
            isSuccess: true,
            loading: false,
          });
          notification.success({
            message: t('api_successMsg'),
            duration: 4,
            description: t('api_addGroupMsg'),
          });
        }
      })
      .catch(error => {
        if (error.response.status === 409) {
          handleApiError(error, error.response.data.data.replace('.', ''), t);
        } else {
          handleApiError(
            error,
            t('api_addError', { label: t('home_menu_group') }),
            t,
          );
        }
        this.setState({
          loading: false,
        });
      });
  };

  render() {
    const { currentStepIndex, isSuccess, loading } = this.state;
    const { t } = this.props;
    return (
      <div>
        <Row>
          <Col span={22} offset={1}>
            <div>
              <div className={styles.step}>
                <Steps style={{ minHeight: 60 }} current={currentStepIndex}>
                  <Step key="GroupType" title={t('label_type')} />
                  <Step key="GroupDetails" title={t('label_details')} />
                  <Step
                    key="AttachingRoles"
                    title={t('label_attaching_Roles')}
                  />
                  <Step key="Done" title={t('label_finish')} />
                </Steps>
              </div>
            </div>
          </Col>
          <Col span={22} offset={1}>
            <div style={{ display: currentStepIndex === 0 ? 'unset' : 'none' }}>
              <SelectGroupType
                setNextStep={this.setNextStep}
                onGroupTypeChange={this.handleGroupTypeChange}
                groupName={this.groupName}
                updateSelectedKeyId={this.updateSelectedKeyId}
                updateSelectedKeyName={this.updateSelectedKeyName}
              />
            </div>

            <div style={{ display: currentStepIndex === 1 ? 'unset' : 'none' }}>
              <GroupDetails
                fetchGroups={this.fetchGroups}
                style={{ marginBottom: '10px' }}
                setPrevStep={this.setPrevStep}
                setNextStep={this.setNextStep}
                onSubmit={this.handleFormSubmit}
              />
            </div>
            <div style={{ display: currentStepIndex === 2 ? 'unset' : 'none' }}>
              <AttachingRoles
                setPrevStep={this.setPrevStep}
                setNextStep={this.setNextStep}
                groupName={this.state.groupName}
                groupDescription={this.state.groupDescription}
                onConfirmShareGroup={this.onConfirmShareGroup}
                onSubmit={this.handleFormSubmit}
              />
            </div>
            <div style={{ display: currentStepIndex === 3 ? 'unset' : 'none' }}>
              <div>
                <Form>
                  <Form.Item
                    wrapperCol={{
                      offset: 7,
                      span: 10,
                    }}
                  >
                    {!loading && isSuccess && (
                      <Result
                        status="success"
                        title={t('label_title_group_adding_successfully')}
                        subTitle={t('label_subTitle_group_adding_successfully')}
                        extra={[
                          <Link
                            to={`/${this.config.appName}/groups/`}
                            key="btnPolicies"
                          >
                            <Button
                              type="primary"
                              key="btnBack"
                              onClick={this.setPrevStep}
                            >
                              {'Done'}
                            </Button>
                          </Link>,
                        ]}
                      />
                    )}
                    {!loading && !isSuccess && (
                      <Result
                        status="error"
                        title={t('label_title_group_adding_failed')}
                        subTitle={t('label_subTitle_group_adding_failed')}
                        extra={[
                          <Button
                            type="primary"
                            key="btnBack"
                            onClick={this.setPrevStep}
                          >
                            {'Back'}
                          </Button>,
                        ]}
                      />
                    )}
                  </Form.Item>
                </Form>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    );
  }
}

export default withConfigContext(withTranslation()(CreateGroup));
